import {
  BehaviorSubject,
  forkJoin,
  Observable,
  ObservableInput,
  of,
} from 'rxjs';
import { map, tap, switchMap, take, concatMap } from 'rxjs/operators';

import {
  CentralApis,
  Endpoint,
  ConfigColumns,
  PageConfig,
  ACTIONS,
} from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';
import {
  DropPermission,
  IDropPermission,
} from '../models/dropPermission.model';
import { Columns, TeamSalesRepService } from '..';
import { Result } from '../interfaces/helpers';
import { VendorSalesRep } from '../models/vendorSalesRep.model';

export class DropPage extends Page<IDropPermission> {
  numberCols: number;

  /**
   * Defines the sections to render
   * based on the drop permission's record type
   */
  displaySections = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    SBU: [
      'propertyDrawing',
      'contactInformation',
      'businessProfile',
      'servicesActive',
      'confirmation',
      'systemInformation',
      'incompleteDropPermissionInformation',
    ],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    SFU: [
      'propertyDrawing',
      'propertyInformation',
      'providerInformation',
      'homeownerInformation',
      'confirmation',
      'systemInformation',
      'incompleteDropPermissionInformation',
    ],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MDU: [
      'contactInformation',
      'providerInformation',
      'confirmation',
      'systemInformation',
      'incompleteDropPermissionInformation',
    ],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MTU: [
      'contactInformation',
      'businessProfile',
      'servicesActive',
      'confirmation',
      'systemInformation',
      'incompleteDropPermissionInformation',
    ],
  };
  /**
   * Defines each column per drop permission's section
   * to be rendered in a detail view
   */
  subSectionDisplayColumns = {
    propertyDrawing: [
      {
        field: 'serviceAddressLink',
        visibleIn: ['SBU', 'SFU'],
        type: 'link',
        editType: 'option',
        isHidden: false,
        visible: true,
        source: 'address360',
        sourceParameter: 'addressId',
        sourceDisplayField: 'serviceAddress',
        searchBoxDebounceTime: 1000,
        update: {
          section: 'propertyDrawing',
          property: 'addressId',
          useId: true,
        },
      },
      {
        field: 'addressString',
        visibleIn: ['SBU', 'SFU'],
        type: 'string',
        editType: 'raw',
        isHidden: false,
      },
      {
        field: 'property',
        visibleIn: ['SBU', 'SFU'],
        type: 'imagePlaceholder',
        editType: 'imagePlaceholder',
        isHidden: false,
      },
      {
        field: 'status',
        visibleIn: ['SBU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'statusReason',
        visibleIn: ['SBU', 'SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'recordType',
        visibleIn: ['SBU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'dropType',
        visibleIn: ['SBU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'isInsideWiringRequired',
        visibleIn: ['SBU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
    ],
    propertyInformation: [
      {
        field: 'additionalPropertyDetails',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'option',
        allowsMultiple: true,
        isHidden: false,
      },
      {
        field: 'dropType',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'additionalComments',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'isInsideWiringRequired',
        visibleIn: ['SFU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
    ],
    homeownerInformation: [
      {
        field: 'occupantFirstname',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantLastname',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantPhone',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'occupantMobilePhone',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'occupantEmail',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'addressPostalCode',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantBuildingOwner',
        colName: 'occupantBuildingOwner-SFU',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantBuildingOwnerPhone',
        colName: 'occupantBuildingOwnerPhone-SFU',
        visibleIn: ['SFU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'consent',
        visibleIn: ['SFU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
    ],
    confirmation: [
      {
        field: 'consent',
        visibleIn: ['MDU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'vendorSalesRepPhone',
        visibleIn: ['SFU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'preferredDeliveryMethod',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
    ],
    contactInformation: [
      {
        field: 'businessName',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantPhone',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'occupantFirstname',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantLastname',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantTitle',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantEmail',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantMobilePhone',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'consent',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'occupantBuildingOwner',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'secondaryContactTitle',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'occupantBuildingOwnerPhone',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'tel',
        mask: '(000) 000-0000',
        placeholder: '(999) 999-9999',
        isHidden: false,
      },
      {
        field: 'buildingOwnerEmail',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'otherProducts',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'status',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'statusReason',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'recordType',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'serviceAddressLink',
        visibleIn: ['MDU', 'MTU'],
        type: 'link',
        editType: 'option',
        isHidden: false,
        visible: true,
        source: 'address360',
        sourceParameter: 'addressId',
        sourceDisplayField: 'serviceAddress',
        searchBoxDebounceTime: 1000,
        update: {
          section: 'propertyDrawing',
          property: 'addressId',
          useId: true,
        },
      },
      {
        field: 'addressString',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'raw',
        isHidden: false,
      },
      {
        field: 'additionalComments',
        visibleIn: ['MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'addressPostalCode',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'customerAvailability',
        visibleIn: ['MDU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'configuration',
        visibleIn: ['MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'preferredInstallationDate',
        visibleIn: ['MDU'],
        type: 'date',
        editType: 'date',
        isHidden: false,
      },
      {
        field: 'preferredInstallationTime',
        visibleIn: ['MDU'],
        type: 'string',
        editType: 'time',
        isHidden: false,
      },
    ],
    providerInformation: [
      {
        field: 'currentInternetProvider',
        colName: 'currentInternetProvider-MDU',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'currentInternetSpeed',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'offerExpireDate',
        visibleIn: ['MDU', 'SFU'],
        type: 'date',
        editType: 'date',
        isHidden: false,
      },
      {
        field: 'currentMobilityProvider',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'contractExpiryDate',
        visibleIn: ['MDU', 'SFU'],
        type: 'date',
        editType: 'date',
        isHidden: false,
      },
      {
        field: 'signalStrength',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'configuration',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'pricing',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'preferredPhoneType',
        visibleIn: ['MDU', 'SFU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
    ],
    businessProfile: [
      {
        field: 'currentProviders',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'option',
        allowsMultiple: true,
        isHidden: false,
      },
      {
        field: 'currentInternetSpeed',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'otherProducts',
        visibleIn: ['SBU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'numberEmployees',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'offerConsent',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
    ],
    servicesActive: [
      {
        field: 'alarmSystem',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'complexServices',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'internet',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'mobility',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'phone',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'tv',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'other',
        visibleIn: ['SBU', 'MTU'],
        type: 'boolean',
        editType: 'boolean',
        isHidden: false,
      },
      {
        field: 'otherServiceActive',
        visibleIn: ['SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
    ],
    systemInformation: [
      {
        field: 'createdByLink',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'link',
        editType: 'link',
        isHidden: false,
      },
      {
        field: 'systemGeneratedComments',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
      {
        field: 'sourceSystem',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: undefined,
        isHidden: false,
      },
      {
        field: 'vendorSalesRep',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'link',
        editType: 'option',
        isHidden: false,
        visible: true,
        source: 'salesrep',
        sourceParameter: 'name',
        sourceDisplayField: 'name',
        update: {
          section: 'systemInformation',
          property: 'vendorSalesRep',
          useId: true,
        },
      },
    ],
    incompleteDropPermissionInformation: [
      {
        field: 'reconciliationStatus',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'option',
        isHidden: false,
      },
      {
        field: 'lastReconciliationStatusUpdateDate',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: undefined,
        isHidden: false,
      },
      {
        field: 'lastReconciliationStatusUpdateBy',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: undefined,
        isHidden: false,
      },
      {
        field: 'comments',
        visibleIn: ['SFU', 'MDU', 'SBU', 'MTU'],
        type: 'string',
        editType: 'string',
        isHidden: false,
      },
    ],
  };

  private teamSalesRep;

  constructor(
    { endpoint }: Endpoint,
    config: PageConfig,
    teamSalesRep: TeamSalesRepService
  ) {
    super(
      (endpoint as CentralApis).activity + 'droppermission',
      PageTitles.drop,
      config
    );
    this.teamSalesRep = teamSalesRep;
    this.numberCols = config.maxCol;

    /**
     * Defines the columns to render for the
     * main drop permission details table
     */
    const mainColumns: ConfigColumns<IDropPermission> = {
      keys: {
        id: '',
        dropPermissionName: '',
        serviceAddressLink: 'link',
        dateFormatted: '',
        recordType: '',
        sourceSystem: '',
        createdBy: '',
        modifiedBy: '',
      },
      hiddenTableFields: [
        'href',
        'contactInformation',
        'businessProfile',
        'serviceActive',
        'confirmation',
        'systemInformation',
        'propertyDrawing',
        'propertyInformation',
        'providerInformation',
        'webSubmissions',
        'incompletePermissionInformation',
        'extra',
      ],
      dropdownFields: ['dropPermissionName', 'recordType', 'sourceSystem'],
    };

    // Initializes the columns for the table
    this.initializeColumns(mainColumns);
  }

  get actions(): string[] {
    return [ACTIONS.update, ACTIONS.delete];
  }

  /**
   * Builds a column object needed to render
   * each drop permission's property
   *
   * @param col column information directly related to a property
   * @param index column index, if greater than this.numberCols it is hidden
   */
  buildColumnDefinition(col: any, index: number, isEditMode: boolean = false) {
    const columnDefinition: Columns = {
      field: col.field,
      elName: col.elName || '',
      colName: col.colName || undefined,
      visible: index < this.numberCols,
      type: !isEditMode ? col.type : col.editType,
      placeholder: col.placeholder || null,
      allowsMultiple: col.allowsMultiple || false,
      hiddenFormField: col.isHidden,
      hiddenTableField: col.isHiddden,
      isEditable: col.editType !== undefined ? isEditMode : false,
      searchBoxDebounceTime: col.searchBoxDebounceTime,
      update: col.update || undefined,
    };
    if (col.mask) {
      columnDefinition.mask = col.mask;
    }
    if (col.source !== undefined) {
      columnDefinition.source = col.source;
      columnDefinition.sourceParameter = col.sourceParameter;
      columnDefinition.sourceDisplayField = col.sourceDisplayField;
    }
    return columnDefinition;
  }

  /**
   * Returns the sections to render from a drop permission
   * based on record type and the values of the drop
   *
   * @param recordType drop permission's record type
   * @param drop drop permission object
   */
  getSections(
    recordType: string,
    drop: DropPermission,
    isEditMode: boolean = false
  ) {
    const sections = Object.keys(this.displaySections).includes(recordType)
      ? this.displaySections[recordType]
      : [];
    return sections.map((section: string) => ({
      sectionLabel: `Drop-PermissionPage.sections.` + section,
      columns: this.getDisplayColumns(section, recordType, isEditMode),
      item: drop,
    }));
  }

  /**
   * Returns the columns to render given an specific
   * section of a drop permission and its record type
   *
   * @param sectionName section of a drop permission, inner object
   * @param recordType drop permission's record type
   */
  getDisplayColumns(
    sectionName: string,
    recordType: string,
    isEditMode: boolean = false
  ) {
    const columns: Columns[] = [];
    const columnObservable = new BehaviorSubject<Columns[]>([]);
    const fields = this.subSectionDisplayColumns[sectionName];
    let idx = 0;
    // eslint-disable-next-line guard-for-in
    for (const field in fields) {
      if (
        fields[field] !== undefined &&
        fields[field] &&
        fields[field].visibleIn.includes(recordType)
      ) {
        columns.push(
          this.buildColumnDefinition(fields[field], idx, isEditMode)
        );
      }
      idx++;
    }
    columnObservable.next(columns);
    return columnObservable;
  }

  /**
   * Retrieves drop permissions from backend and
   * creates a list of objects based on DropPermission
   *
   * @param queryString string with query parameters
   */
  fetch(queryString?: any): Observable<Result<DropPermission>> {
    if (!queryString) {
      queryString = {
        desc: true,
      };
    }
    return this.getItems<Result<DropPermission>>(queryString).pipe(
      concatMap((res: Result<DropPermission>) => {
        // Filter CPMS ids to issue only one request to rep roster

        let vendorCPMSIDs = Object.keys(res.items).map(
          (i) => res.items[i].createdBy
        );
        vendorCPMSIDs = vendorCPMSIDs.filter(
          (x) => x !== undefined && x !== 'undefined' && x !== '' && x !== null
        );
        vendorCPMSIDs = vendorCPMSIDs.filter(
          (item, pos) => vendorCPMSIDs.indexOf(item) === pos
        );

        const dropsWithSalesInfo: [
          ObservableInput<Result<DropPermission>>,
          ObservableInput<Result<VendorSalesRep>>
        ] = [
          of(res),
          this.teamSalesRep.fetch(undefined, {
            condition: 'cpmsIDs',
            // eslint-disable-next-line @typescript-eslint/quotes
            value: `${vendorCPMSIDs.join("','")}`,
          }),
        ];

        return forkJoin(dropsWithSalesInfo);
      }),
      map((res) => {
        const items: DropPermission[] = [];
        const dropsResult: Result<DropPermission> = res[0];
        const salesRepsResult: Result<VendorSalesRep> = res[1];
        for (const key in dropsResult.items) {
          if (dropsResult.items.hasOwnProperty(key)) {
            const salesRep: VendorSalesRep =
              salesRepsResult.items.find(
                (rep: VendorSalesRep) =>
                  rep.cpms === dropsResult.items[key].createdBy
              ) || null;
            const drop = new DropPermission(dropsResult.items[key], salesRep);
            items.push(drop);
          }
        }
        dropsResult.items = items;
        return dropsResult;
      }),
      tap((drops) => {
        this.pageItems.next(drops.items);
      })
    );
  }

  fetchById(id: string): Observable<DropPermission> {
    return this.getItem(id).pipe(
      map((data: IDropPermission) => new DropPermission(data))
    );
  }

  update(item: IDropPermission): Observable<DropPermission> {
    let updatedDrop: IDropPermission[];

    return this.items.pipe(
      take(1),
      switchMap((drops) => {
        if (!drops || drops.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(drops);
        }
      }),
      switchMap((drops) => {
        const dropIndex = drops.findIndex((i) => i.id === item.id);
        updatedDrop = [...drops];
        if (dropIndex !== -1) {
          const oldDrop = updatedDrop[dropIndex];
          item.id = oldDrop.id;
        }
        updatedDrop[dropIndex] = this.createFromDTO(item);

        return this.updateItems(updatedDrop[dropIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedDrop);
      })
    );
  }

  createFromDTO(item): DropPermission {
    return new DropPermission(item);
  }
}
